import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/hooks/useAuth";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { TenantProvider, useTenant } from "@/contexts/TenantContext";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { CustomerAuthProvider } from "@/contexts/CustomerAuthContext";

// Lazy load page components for code splitting
const Index = lazy(() => import("./pages/Index"));
const CustomersPage = lazy(() => import("./pages/CustomersPage"));
const ServicesPage = lazy(() => import("./pages/ServicesPage"));
const ProductsPage = lazy(() => import("./pages/ProductsPage"));
const AppointmentsPage = lazy(() => import("./pages/AppointmentsPage"));
const SmsHistoryPage = lazy(() => import("./pages/SmsHistoryPage"));
const ViberHistoryPage = lazy(() => import("./pages/ViberHistoryPage"));
const AnalyticsPage = lazy(() => import("./pages/AnalyticsPage"));
const AuthPage = lazy(() => import("./pages/AuthPage"));
const ProfilePage = lazy(() => import("./pages/ProfilePage"));
const SuperAdminPage = lazy(() => import("./pages/SuperAdminPage"));
const LandingPage = lazy(() => import("./pages/LandingPage"));
const UnifiedLandingPage = lazy(() => import("./pages/UnifiedLandingPage"));
const ExpensesPage = lazy(() => import("./pages/ExpensesPage"));
const DebtsPage = lazy(() => import("./pages/DebtsPage"));
const ReceiptsPage = lazy(() => import("./pages/ReceiptsPage"));
const SettingsPage = lazy(() => import("./pages/SettingsPage"));
const BillingPage = lazy(() => import("./pages/BillingPage"));
const LocationsPage = lazy(() => import("./pages/LocationsPage"));
const LegalPage = lazy(() => import("./pages/LegalPage"));
const BookingPage = lazy(() => import("./pages/BookingPage"));
const BookingStatusPage = lazy(() => import("./pages/BookingStatusPage"));
const CustomerLoginPage = lazy(() => import("./pages/CustomerLoginPage"));
const CustomerBookingsPage = lazy(() => import("./pages/CustomerBookingsPage"));
const CustomerProfilePage = lazy(() => import("./pages/CustomerProfilePage"));
const VerticalLandingPage = lazy(() => import("./pages/VerticalLandingPage"));
const NotFound = lazy(() => import("./pages/NotFound"));
const CookieConsent = lazy(() => import("./components/CookieConsent"));
const CommandPalette = lazy(() => import("./components/CommandPalette").then(m => ({ default: m.CommandPalette })));

// Loading component for Suspense fallback
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
  </div>
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      refetchOnWindowFocus: false,
      retry: 2,
    },
  },
});

const TenantResolver = ({ children }: { children: React.ReactNode }) => {
  const { tenant, isLoading, isMainDomain, isUnifiedHomepage } = useTenant();
  const { profile } = useAuth();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Unified homepage at enviflow.gr
  if (isUnifiedHomepage) {
    return (
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/legal/:slug" element={<LegalPage />} />
          <Route path="/" element={<UnifiedLandingPage />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    );
  }

  // If on main domain (e.g., enviflow.gr or localhost)
  if (isMainDomain) {
    return (
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/auth" element={<AuthPage />} />
          <Route
            path="/admin/*"
            element={
              <ProtectedRoute>
                {profile?.role === 'super_admin' ? (
                  <SuperAdminPage />
                ) : (
                  <Navigate to="/" replace />
                )}
              </ProtectedRoute>
            }
          />
          <Route path="/legal/:slug" element={<LegalPage />} />
          <Route path="/for/:vertical" element={<VerticalLandingPage />} />
          <Route path="/" element={<LandingPage />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    );
  }

  // If on a tenant domain/subdomain but tenant not found
  if (!tenant) {
    return (
      <Suspense fallback={<PageLoader />}>
        <NotFound />
      </Suspense>
    );
  }

  // If on a valid tenant domain, render public booking routes alongside authenticated ones
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/book" element={<BookingPage />} />
        <Route path="/book/status/:token" element={<BookingStatusPage />} />
        <Route path="/portal" element={<CustomerAuthProvider><CustomerLoginPage /></CustomerAuthProvider>} />
        <Route path="/portal/bookings" element={<CustomerAuthProvider><CustomerBookingsPage /></CustomerAuthProvider>} />
        <Route path="/portal/profile" element={<CustomerAuthProvider><CustomerProfilePage /></CustomerAuthProvider>} />
        <Route path="*" element={<>{children}</>} />
      </Routes>
    </Suspense>
  );
};

const App = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <TenantProvider>
          <AuthProvider>
            <Toaster />
            <Sonner />
            <BrowserRouter>
              <Suspense fallback={null}>
                <CookieConsent />
              </Suspense>
              <TenantResolver>
                <Suspense fallback={<PageLoader />}>
                  <Routes>
                    <Route path="/auth" element={<AuthPage />} />
                    <Route path="/" element={<ProtectedRoute><Index /></ProtectedRoute>} />
                    <Route path="/customers" element={<ProtectedRoute><CustomersPage /></ProtectedRoute>} />
                    <Route path="/services" element={<ProtectedRoute><ServicesPage /></ProtectedRoute>} />
                    <Route path="/products" element={<ProtectedRoute><ProductsPage /></ProtectedRoute>} />
                    <Route path="/appointments" element={<ProtectedRoute><AppointmentsPage /></ProtectedRoute>} />
                    <Route path="/sms-history" element={<ProtectedRoute><SmsHistoryPage /></ProtectedRoute>} />
                    <Route path="/viber-history" element={<ProtectedRoute><ViberHistoryPage /></ProtectedRoute>} />
                    <Route path="/expenses" element={<ProtectedRoute><ExpensesPage /></ProtectedRoute>} />
                    <Route path="/debts" element={<ProtectedRoute><DebtsPage /></ProtectedRoute>} />
                    <Route path="/receipts" element={<ProtectedRoute><ReceiptsPage /></ProtectedRoute>} />
                    <Route path="/analytics" element={<ProtectedRoute><AnalyticsPage /></ProtectedRoute>} />
                    <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
                    <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
                    <Route path="/billing" element={<ProtectedRoute><BillingPage /></ProtectedRoute>} />
                    <Route path="/locations" element={<ProtectedRoute><LocationsPage /></ProtectedRoute>} />
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                  <CommandPalette />
                </Suspense>
              </TenantResolver>
            </BrowserRouter>
          </AuthProvider>
        </TenantProvider>
      </TooltipProvider>
    </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
